import { AxiosResponse } from 'axios';

import { Project } from '@qality/common-model';
import {
  ProjectService,
  StorageOfUniqueServices,
} from '@qality/common-service';

export default {
  async getProject(projectKey: string, expand?: string): Promise<Project> {
    let url = `/rest/api/2/project/${projectKey}`;

    if (expand) {
      url += `expand=${expand}`;
    }
    const response = await StorageOfUniqueServices.get('API').request(url);
    return JSON.parse(response.body);
  },

  async fetchProjects(
    projectIds: Array<string>
  ): Promise<{ [projectId: string]: Project }> {
    const NOT_FOUND_STATUS = 404;

    const projectPromises = [];
    const projectMap = {};

    projectIds.forEach((projectId) => {
      projectPromises.push(
        ProjectService.getProject(projectId).catch((e) => {
          if (e.xhr?.status === NOT_FOUND_STATUS) {
            return null;
          }
          throw new Error(`Could not fetch project with id ${projectId}!`);
        })
      );
    });

    const projects = await Promise.all(projectPromises);

    projects.forEach((project) => {
      if (project) {
        projectMap[project.id] = project;
      }
    });

    return projectMap;
  },

  setProjectPropertyAsUser(
    projectKey: string,
    propertyKey: string,
    data: {
      enabled: boolean;
      isPluginEnabled: boolean;
    }
  ): Promise<AxiosResponse> {
    return StorageOfUniqueServices.get('API').request({
      url: '/rest/api/2/project/' + projectKey + '/properties/' + propertyKey,
      type: 'PUT',
      contentType: 'application/json',
      data: JSON.stringify(data),
    });
  },

  getProjectPropertyNoRetry(projectKey: string, propertyKey: string): boolean {
    return StorageOfUniqueServices.get('API').noRetry({
      url: '/rest/api/2/project/' + projectKey + '/properties/' + propertyKey,
      type: 'GET',
    });
  },

  deleteProjectProperty(
    projectKey: string,
    propertyKey: string
  ): Promise<AxiosResponse> {
    return StorageOfUniqueServices.get('API').noRetry({
      url: '/rest/api/2/project/' + projectKey + '/properties/' + propertyKey,
      type: 'DELETE',
    });
  },

  async isQAlityConfiguredInGivenProject(projectKey: string): Promise<boolean> {
    const project = await this.getProject(projectKey);
    return new Project(project).doesGivenProjectHaveQAlityTest();
  },

  async isQAlityConfiguredInAnyProject(): Promise<boolean> {
    return await StorageOfUniqueServices.get(
      'ProjectServiceUnique'
    ).isQAlityConfiguredInAnyProject();
  },

  async getAnyProjectWithConfiguredQAlity(): Promise<Project> {
    return await StorageOfUniqueServices.get(
      'ProjectServiceUnique'
    ).getAnyProjectWithConfiguredQAlity();
  },
};
